import type { DateValue, RangeValue } from '@heroui/react';
import { EProductDuration, ESubSource } from '@quesmed/types/models';
import { ESubType } from '@quesmed/types/resolvers/query/admin';
import { create } from 'zustand';
import { EDateFilter, EProductCategory } from '@/config';
import type { OmitFunctions } from '@/types';
import { getDateRange } from '@/utils';
import { createSelectors } from './selector';

interface ProductEarningsState {
  duration: EProductDuration;
  subType: ESubType;
  source: ESubSource;
  universityId: number | null;
  searchText: string;
  useNetEarning: boolean;
  productCategory: EProductCategory;
  dateFilter: EDateFilter;
  range: { from: Date | null | number; to: Date | null | number };
  dateRange: RangeValue<DateValue> | undefined;
  isFilterDrawerOpen: boolean;
  isLoading: boolean;

  setDuration: (duration: EProductDuration) => void;
  setSubType: (subType: ESubType) => void;
  setSource: (source: ESubSource) => void;
  setUniversityId: (universityId: number | null) => void;
  setSearchText: (searchText: string) => void;
  setUseNetEarning: (useNetEarning: boolean) => void;
  setProductCategory: (productCategory: EProductCategory) => void;
  setDateFilter: (dateFilter: EDateFilter) => void;
  setIsFilterDrawerOpen: (isOpen: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setRange: (range: ProductEarningsState['range']) => void;
  setDateRange: (dateRange: ProductEarningsState['dateRange']) => void;
  reset: () => void;
}

const initialState: OmitFunctions<ProductEarningsState> = {
  duration: EProductDuration.ALL,
  subType: ESubType.ALL,
  source: ESubSource.STRIPE,
  universityId: null,
  searchText: '',
  useNetEarning: true,
  productCategory: EProductCategory.ALL,
  dateFilter: EDateFilter.THIS_MONTH,
  range: getDateRange(EDateFilter.THIS_MONTH),
  dateRange: undefined,
  isFilterDrawerOpen: false,
  isLoading: false,
};

export const productEarningsState = create<ProductEarningsState>()((set) => ({
  ...initialState,
  setDuration: (duration) => set({ duration }),
  setSubType: (subType) => set({ subType }),
  setSource: (source) => set({ source }),
  setUniversityId: (universityId) => set({ universityId }),
  setSearchText: (searchText) => set({ searchText }),
  setUseNetEarning: (useNetEarning) => set({ useNetEarning }),
  setProductCategory: (productCategory) => set({ productCategory }),
  setDateFilter: (dateFilter) => set({ dateFilter }),
  setIsFilterDrawerOpen: (isOpen) => set({ isFilterDrawerOpen: isOpen }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setRange: (range) => set({ range }),
  setDateRange: (dateRange) => set({ dateRange }),
  reset: () => set(initialState),
}));

export const useProductEarningsState = () => productEarningsState((state) => state);
export const useProductEarnings = createSelectors(productEarningsState);
